import { useState, useRef, useEffect } from "react";

const DEVICES = [
  { label: "iPhone SE", width: 375, height: 667 },
  { label: "iPhone 14", width: 390, height: 844 },
  { label: "iPad", width: 768, height: 1024 },
  { label: "Laptop", width: 1024, height: 768 },
  { label: "Desktop", width: 1440, height: 900 },
];

function DevicePreview({ children }) {
  const [activeDevice, setActiveDevice] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownOpen]);

  // Show DevTools in dev mode OR when ?preview=pirate2024 is in the URL
  const params = new URLSearchParams(window.location.search);
  const isDev = process.env.NODE_ENV === "development";
  const hasPreviewKey = params.get("preview") === "pirate2024";
  const showDevTools = isDev || hasPreviewKey;

  if (!showDevTools) {
    return children;
  }

  // Inside iframe preview — render children without toolbar
  if (params.get("_devpreview") === "1") {
    return children;
  }

  // When a device is active, show it in an iframe
  if (activeDevice) {
    const scale = Math.min(
      (window.innerWidth - 80) / activeDevice.width,
      (window.innerHeight - 120) / activeDevice.height,
      1
    );

    return (
      <div style={{ background: "#0a0a14", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Toolbar */}
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 99999,
          background: "rgba(10,10,20,0.97)", borderBottom: "1px solid rgba(212,175,55,0.25)",
          padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
          backdropFilter: "blur(12px)", flexWrap: "wrap"
        }}>
          {DEVICES.map((device) => (
            <button
              key={device.width}
              onClick={() => setActiveDevice(device)}
              style={{
                padding: "6px 14px", borderRadius: "8px", border: "1px solid",
                fontSize: "12px", cursor: "pointer", transition: "all 0.2s",
                fontFamily: "Inter, sans-serif",
                background: activeDevice.width === device.width ? "#d4af37" : "rgba(30,30,50,0.6)",
                color: activeDevice.width === device.width ? "#0a0a14" : "#e8e0d0",
                borderColor: activeDevice.width === device.width ? "#d4af37" : "rgba(212,175,55,0.15)",
                fontWeight: activeDevice.width === device.width ? "700" : "400",
              }}
            >
              {device.label} <span style={{ opacity: 0.5, fontSize: "10px", marginLeft: "4px" }}>{device.width}×{device.height}</span>
            </button>
          ))}
          <button
            onClick={() => setActiveDevice(null)}
            style={{
              padding: "6px 14px", borderRadius: "8px", fontSize: "12px", cursor: "pointer",
              background: "rgba(30,30,50,0.6)", color: "#e8e0d0",
              border: "1px solid rgba(212,175,55,0.15)", fontFamily: "Inter, sans-serif",
              transition: "all 0.2s",
            }}
          >
            ✕ Schließen
          </button>
        </div>

        {/* Device frame */}
        <div style={{ marginTop: "70px", padding: "20px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {/* Device label */}
          <div style={{
            color: "rgba(212,175,55,0.6)", fontSize: "11px", fontFamily: "Inter, sans-serif",
            marginBottom: "10px", letterSpacing: "0.1em"
          }}>
            {activeDevice.label} — {activeDevice.width} × {activeDevice.height}px
            {scale < 1 && <span style={{ marginLeft: "8px", opacity: 0.5 }}>({Math.round(scale * 100)}% Zoom)</span>}
          </div>

          {/* Device bezel */}
          <div style={{
            border: "3px solid rgba(212,175,55,0.2)", borderRadius: "20px",
            overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            transform: `scale(${scale})`, transformOrigin: "top center",
            width: activeDevice.width, height: activeDevice.height,
          }}>
            <iframe
              src={window.location.origin + "/?_devpreview=1" + (hasPreviewKey ? "&preview=pirate2024" : "")}
              title={`Preview: ${activeDevice.label}`}
              style={{
                width: activeDevice.width, height: activeDevice.height,
                border: "none", display: "block",
                background: "#0a1828",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Normal mode — just show the toggle button + children
  return (
    <>
      <div ref={dropdownRef} style={{ position: "fixed", top: "88px", right: "16px", zIndex: 99999 }}>
        <button
          onClick={() => setDropdownOpen((v) => !v)}
          style={{
            background: "rgba(10,10,20,0.95)", color: "#d4af37",
            border: "1px solid rgba(212,175,55,0.3)", borderRadius: "10px",
            padding: "8px 16px", fontSize: "12px", fontWeight: "700", cursor: "pointer",
            fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: "6px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)", transition: "all 0.2s",
          }}
        >
          <span>⚙</span>
          <span>DevTools</span>
          <span style={{ transition: "transform 0.2s", transform: dropdownOpen ? "rotate(180deg)" : "none" }}>▾</span>
        </button>

        {dropdownOpen && (
          <div style={{
            marginTop: "8px", background: "rgba(10,10,20,0.98)", border: "1px solid rgba(212,175,55,0.25)",
            borderRadius: "14px", padding: "12px", minWidth: "220px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.7)", backdropFilter: "blur(12px)",
          }}>
            <div style={{
              fontSize: "9px", color: "rgba(212,175,55,0.4)", letterSpacing: "0.2em",
              textTransform: "uppercase", padding: "0 8px 8px", fontFamily: "Inter, sans-serif",
            }}>
              Geräte-Vorschau
            </div>
            {DEVICES.map((device) => (
              <button
                key={device.width}
                onClick={() => { setActiveDevice(device); setDropdownOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "8px 10px", borderRadius: "8px", fontSize: "12px",
                  cursor: "pointer", border: "1px solid rgba(212,175,55,0.08)",
                  background: "rgba(30,30,50,0.3)", color: "#e8e0d0",
                  fontFamily: "Inter, sans-serif", marginBottom: "4px",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.target.style.background = "rgba(30,30,50,0.7)"; e.target.style.borderColor = "rgba(212,175,55,0.3)"; }}
                onMouseLeave={(e) => { e.target.style.background = "rgba(30,30,50,0.3)"; e.target.style.borderColor = "rgba(212,175,55,0.08)"; }}
              >
                {device.label}
                <span style={{ marginLeft: "8px", opacity: 0.4, fontSize: "10px" }}>{device.width}×{device.height}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {children}
    </>
  );
}

export default DevicePreview;
